import { Section } from '@/components/shared/Content';
import { useIsClient } from '@/hooks/useIsClient';
import arrow from '@/static/assets/giveback/hiw-horizontal-arrow.png';
import { Box, Flex } from '@lemonade-hq/cdk';
import parse from 'html-react-parser';
import Image from 'next/image';
import styled from 'styled-components';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import type { FC } from 'react';
import { GBSectionParagraph, GBSectionTitle } from './StyledElements';

const Container = styled(Flex)`
  gap: 40px;

  ${MEDIA_QUERIES.tabletAndDesktop`
      gap: 60px;
  `};
`;

const Step = styled(Flex)`
  ${MEDIA_QUERIES.tabletAndDesktop`
    width: 330px;
  `};
`;

const StepContent = styled(GBSectionParagraph)`
  font-size: 1.6rem !important;
  line-height: 145% !important;
  text-wrap: balance;
  text-align: left;
  padding-left: 12px;

  ${MEDIA_QUERIES.tabletAndDesktop`
      text-align: center;
      padding-left: 0;
  `};
`;

const WorkSectionTitle = styled(GBSectionTitle)`
  width: 100%;
  text-align: left;
  padding: 0;
  ${MEDIA_QUERIES.tabletAndDesktop`
      text-align: center;
  `};
`;

const ArrowWrapper = styled(Box)`
  position: absolute;
  left: -60px;
  top: 90px;
`;

const HowItWorksStep: FC<{ step: Step; index: number }> = ({ step, index }) => {
  const isClient = useIsClient();

  return (
    <Flex key={step.text} position="relative" width={1}>
      {index !== 0 && (
        <ArrowWrapper display={['none', 'none', 'block']}>
          <Image
            src={arrow}
            width={50}
            height={12}
            alt="arrow image"
            style={{
              flexShrink: 0,
              pointerEvents: 'none',
            }}
          />
        </ArrowWrapper>
      )}
      <Step flexDirection={['row', 'column']} alignItems="center">
        <Box display={['block', 'none']}>
          <Image src={step.mobileImage} height={72} width={72} style={{ pointerEvents: 'none' }} alt="" />
        </Box>
        <Box display={['none', 'block']}>
          <Image src={step.image} height={165} width={165} style={{ pointerEvents: 'none' }} alt="" />
        </Box>
        {isClient && (
          <Box mt={[0, 20]}>
            <StepContent>{parse(step.text)}</StepContent>
          </Box>
        )}
      </Step>
    </Flex>
  );
};

interface Step {
  text: string;
  image: StaticImageData;
  mobileImage: StaticImageData;
}

export interface HowItWorksProps {
  title: string;
  steps: Step[];
}

const HowItWorks: FC<HowItWorksProps> = ({ title, steps }) => {
  return (
    <Section px={[20, 0]} py={[50, 50, 96]} darkBackground>
      <WorkSectionTitle>{title}</WorkSectionTitle>

      <Container flexDirection={['column', 'column', 'row']} alignItems={['center', 'start']} mt={[40, 48]}>
        {steps.map((step, index) => (
          <HowItWorksStep key={step.text} step={step} index={index} />
        ))}
      </Container>
    </Section>
  );
};

export default HowItWorks;
